import React, { useState, useEffect, useRef } from "react";
import { fetchAdminConfig, clearPlaceholderBackground, bookImageSrc, bookImageFallback, bookImageBackground } from "./config";
import "../styles/Book.css";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaSync, FaBookOpen, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import HeyzineService from "./heyzineService";
import { S3Client, PutObjectCommand } from "@aws-sdk/client-s3";


// Label shown for K12; selecting it lists classes from all K12 cores together
const K12_ALL_CORES = "NCERT";
const Book = () => {
  const [books, setBooks] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedCore, setSelectedCore] = useState("All");
  const [selectedMainCategory, setSelectedMainCategory] = useState("All");
  const [selectedSubCategory, setSelectedSubCategory] = useState("All");
  const [coreMap, setCoreMap] = useState({});
  const [categoryMap, setCategoryMap] = useState({});
  const [subCategoryMap, setSubCategoryMap] = useState({});
  const [visibleCount, setVisibleCount] = useState(30);
  const [k12Level, setK12Level] = useState("All");
  const [updatingBooks, setUpdatingBooks] = useState({});
  const [showScrollArrow, setShowScrollArrow] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const scrollContainerRef = useRef(null);
  const navigate = useNavigate();

  // Heyzine API Configuration
  const HEYZINE_API_KEY = process.env.REACT_APP_HEYZINE_API_KEY;

  // AWS S3 Configuration
  const AWS_ACCESS_KEY_ID = process.env.REACT_APP_AWS_ACCESS_KEY_ID;
  const AWS_SECRET_ACCESS_KEY = process.env.REACT_APP_AWS_SECRET_ACCESS_KEY;
  const AWS_REGION = "ap-south-1";
  const S3_BUCKET = "easylearn";
  const S3_FOLDER = "EasyLearn V2/classroomFiles/";

  const savedInterests =
    JSON.parse(localStorage.getItem("selectedInterests")) || [];
  const userRole = localStorage.getItem("userRole");
  const isSuperAdmin = userRole === "superadmin" || savedInterests.includes("All");
  const isAllSelected = isSuperAdmin;
  
  // ✅ Admin configurations - loaded from server
  const [adminCourseVisibility, setAdminCourseVisibility] = useState({});
  const [adminFreeBookSettings, setAdminFreeBookSettings] = useState({});
  const [configLoaded, setConfigLoaded] = useState(false);
  const [booksStatus, setBooksStatus] = useState("loading"); // loading | done
  const [failedSources, setFailedSources] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);
  
  const mainCategories = [
    "K12",
    "Mathematics",
    "Vocational",
    "Industry",
    "Technology",
    "Agriculture",
    "UPSC",
    "Medical",
    "Management",
    "Leadership"
  ];

  // Instantiate HeyzineService
  const heyzineService = new HeyzineService(HEYZINE_API_KEY);

  // Instantiate AWS S3 Client
  const s3Client = new S3Client({
    region: AWS_REGION,
    credentials: {
      accessKeyId: AWS_ACCESS_KEY_ID,
      secretAccessKey: AWS_SECRET_ACCESS_KEY,
    },
  });

  // ✅ NEW: Load admin configuration from server on component mount
  useEffect(() => {
    const loadAdminConfig = async () => {
      try {
        const config = await fetchAdminConfig();
        if (config.courseVisibility) setAdminCourseVisibility(config.courseVisibility);
        if (config.freeBookSettings) setAdminFreeBookSettings(config.freeBookSettings);
      } finally {
        setConfigLoaded(true);
      }
    };

    loadAdminConfig();
  }, []);

  // Helper function to check if a category should be visible for normal users
  const isCategoryVisible = (category) => {
    if (isSuperAdmin) return true; // Superadmin sees everything
    
    // ✅ Wait for config to load
    if (!configLoaded) return false;
    
    // Check if admin has hidden this category for normal users
    const visibility = adminCourseVisibility[category];
    // ✅ Only show if explicitly set to true
    return visibility === true;
  };

  // Helper function to check if a book should be free based on admin settings
  const isBookFreeByAdmin = (book) => {
    if (isSuperAdmin) return false; // Superadmin doesn't get free books override
    
    const category = book.category || book.core || "";
    const freeSettings = adminFreeBookSettings[category];
    
    if (!freeSettings || !freeSettings.enabled) return false;
    
    // Check if all books in category are free
    if (freeSettings.allFree) return true;
    
    // Check if this specific book is in the free list
    return freeSettings.freeBooks && freeSettings.freeBooks.includes(book.pdf_name);
  };

  // Helper function to apply admin free settings to book
  const applyAdminSettings = (book) => {
    if (isBookFreeByAdmin(book)) {
      return {
        ...book,
        price: "0",
        original_price: book.price, // Store original price for reference
        admin_free: true // Mark as admin-set free
      };
    }
    return book;
  };

  useEffect(() => {
    const savedInterests = JSON.parse(localStorage.getItem("selectedInterests")) || [];
    const userRole = localStorage.getItem("userRole");
    const isSuperAdminUser = userRole === "superadmin" || savedInterests.includes("All");
    
    // For regular users with only Technology, auto-select after books load
    if (!isSuperAdminUser && savedInterests.length === 1 && savedInterests[0] === "Technology" && books.length > 0) {
      setSelectedCategory("Technology");
      setSelectedCore("All");
    } else if (books.length === 0) {
      // Don't set category until books are loaded
      return;
    } else {
      setSelectedCategory("All");
      setSelectedCore("All");
    }
    
    setSelectedMainCategory("All");
    setSelectedSubCategory("All");
    setVisibleCount(30);
    setK12Level("All");
  }, [books.length]);

  useEffect(() => {
    const fetchBooks = async () => {
      try {
        const fetchData = async (url, source, account_id, reg_id) => {
          const res = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: new URLSearchParams({ account_id, reg_id }),
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          return Array.isArray(data.data)
            ? data.data.map((b) => ({ ...b, source }))
            : [];
        };

        // Progressive loading - fetch and display books as they arrive
        const fetchAndAddBooks = async (url, source, account_id, reg_id) => {
          try {
            const newBooks = await fetchData(url, source, account_id, reg_id);
            if (newBooks.length > 0) {
              setBooks(prevBooks => {
                // Apply admin settings to new books
                const booksWithAdminSettings = newBooks.map(book => applyAdminSettings(book));
                const updatedBooks = [...prevBooks, ...booksWithAdminSettings];
                // Update maps progressively
                updateMapsForBooks(updatedBooks);
                return updatedBooks;
              });
            }
          } catch (err) {
            console.error(`Error fetching ${source} books:`, err);
            setFailedSources(prev => [...prev, source]);
          }
        };

        // Function to update core, category, and subcategory maps
        const updateMapsForBooks = (allBooks) => {
          const coreMapTemp = {};
          const categoryMapTemp = {};
          const subCatMapTemp = {};

          allBooks.forEach((book) => {
            const main = book.source || "";
            const core = book.core_name || book.voc_core_name || "";
            const category =
              book.category_name || book.voc_category_name || book.sub_category_name || "";
            const subCat = book.sub_category_name || book.voc_sub_category_name || "";

            if (!coreMapTemp[main]) coreMapTemp[main] = new Set();
            if (!categoryMapTemp[main]) categoryMapTemp[main] = {};
            if (!subCatMapTemp[main]) subCatMapTemp[main] = {};

            if (core && core.trim() !== "" && core !== "All") {
              coreMapTemp[main].add(core);

              if (!categoryMapTemp[main][core])
                categoryMapTemp[main][core] = new Set();
              if (category && category.trim() !== "" && category !== "All") {
                categoryMapTemp[main][core].add(category);

                if (!subCatMapTemp[main][core])
                  subCatMapTemp[main][core] = {};
                if (!subCatMapTemp[main][core][category])
                  subCatMapTemp[main][core][category] = new Set();

                if (subCat && subCat.trim() !== "" && subCat !== "All") {
                  subCatMapTemp[main][core][category].add(subCat);
                }
              }
            }
          });

          const dynamicCoreMap = {};
          Object.keys(coreMapTemp).forEach((k) => {
            dynamicCoreMap[k] = Array.from(coreMapTemp[k]);
          });
          setCoreMap(dynamicCoreMap);

          const dynamicCategoryMap = {};
          Object.keys(categoryMapTemp).forEach((sourceKey) => {
            dynamicCategoryMap[sourceKey] = {};
            Object.keys(categoryMapTemp[sourceKey]).forEach((coreKey) => {
              dynamicCategoryMap[sourceKey][coreKey] = Array.from(
                categoryMapTemp[sourceKey][coreKey]
              );
            });
          });
          setCategoryMap(dynamicCategoryMap);

          const dynamicSubCatMap = {};
          Object.keys(subCatMapTemp).forEach((sourceKey) => {
            dynamicSubCatMap[sourceKey] = {};
            Object.keys(subCatMapTemp[sourceKey]).forEach((coreKey) => {
              dynamicSubCatMap[sourceKey][coreKey] = {};
              Object.keys(subCatMapTemp[sourceKey][coreKey]).forEach((catKey) => {
                dynamicSubCatMap[sourceKey][coreKey][catKey] = Array.from(
                  subCatMapTemp[sourceKey][coreKey][catKey]
                );
              });
            });
          });
          setSubCategoryMap(dynamicSubCatMap);
        };

        setBooks([]);
        setFailedSources([]);
        setBooksStatus("loading");

        // Fetch all categories in parallel - they will display as they load
        Promise.allSettled([
          fetchAndAddBooks(
            "https://midc.easylearn.org.in/AI_Controller/get_courseware_list",
            "Industry",
            "96",
            "5"
          ),
          fetchAndAddBooks(
            "https://technology.skillmaster.net.in/AI_Controller/get_courseware_list",
            "Technology",
            "96",
            "5"
          ),
          fetchAndAddBooks(
            "https://agriculture.skillmaster.net.in/AI_Controller/get_courseware_list",
            "Agriculture",
            "96",
            "5"
          ),
          fetchAndAddBooks(
            "https://mathematics.easylearn.net.in/AI_Controller/get_courseware_list",
            "Mathematics",
            "5",
            "1"
          ),
          fetchAndAddBooks(
            "https://vocational.skillmaster.net.in/AI_Controller/get_courseware_list",
            "Vocational",
            "96",
            "5"
          ),
          fetchAndAddBooks(
            "https://upsc.skillmaster.net.in/AI_Controller/get_courseware_list",
            "UPSC",
            "96",
            "5"
          ),
          fetchAndAddBooks(
            "https://k12.easylearn.org.in/AI_Controller/get_courseware_list",
            "K12",
            "96",
            "5"
          ),
          fetchAndAddBooks(
            "https://medical.skillmaster.net.in/AI_Controller/get_courseware_list",
            "Medical",
            "10",
            "2"
          ),
          fetchAndAddBooks(
            "https://management.skillmaster.net.in/AI_Controller/get_courseware_list",
            "Management",
            "31",
            "5"
          ),
          fetchAndAddBooks(
            "https://leadership.skillmaster.net.in/AI_Controller/get_courseware_list",
            "Leadership",
            "96",
            "5"
          ),
        ]).finally(() => setBooksStatus("done"));
      } catch (err) {
        console.error("Error fetching books:", err);
      }
    };

    fetchBooks();
  }, [reloadKey]);

  // Asli scroll hone wala element: desktop pe container, mobile pe pills ka group
  const getFilterScroller = () => {
    const container = scrollContainerRef.current;
    if (!container) return null;
    if (window.innerWidth <= 768) {
      const group = Array.from(container.children).find(
        (el) => !el.classList.contains("search-box")
      );
      return group || container;
    }
    return container;
  };

  // Check scroll overflow to show/hide ">>" arrow
  const checkScrollOverflow = () => {
    const container = getFilterScroller();
    if (!container) return;

    const { scrollWidth, clientWidth, scrollLeft } = container;
    // Show arrow only if there's content to scroll on the right
    // Adding 10px tolerance for edge cases
    const maxScroll = scrollWidth - clientWidth;
    const hasOverflow = maxScroll > 4;

    // Right arrow: sirf jab right me aur pills bachi hon
    setShowScrollArrow(hasOverflow && scrollLeft < maxScroll - 4);
    // Left arrow: sirf jab pehle se scroll ho chuka ho
    setShowLeftArrow(hasOverflow && scrollLeft > 4);
  };

  // Left/right arrow buttons for filter pills (desktop + mobile)
  const scrollFilters = (direction) => {
    const scroller = getFilterScroller();
    if (!scroller) return;
    scroller.scrollBy({
      left: direction * (window.innerWidth <= 768 ? 200 : 300),
      behavior: "smooth",
    });
  };

  // Desktop: mouse wheel scrolls filter pills horizontally
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const onWheel = (e) => {
      if (window.innerWidth <= 768) return;
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) return;
      const goingRight = e.deltaY > 0;
      if ((goingRight && el.scrollLeft >= max - 1) || (!goingRight && el.scrollLeft <= 0)) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // Check on mount, resize, and when filters change
  useEffect(() => {
    checkScrollOverflow();
    
    // Add resize listener
    window.addEventListener('resize', checkScrollOverflow);
    
    // Check after a small delay to ensure DOM is updated
    const timer = setTimeout(checkScrollOverflow, 100);

    // Pills load/change hone par (API se categories aane par) bhi dobara check
    const container = scrollContainerRef.current;
    let resizeObserver;
    let mutationObserver;
    if (container) {
      if (typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(checkScrollOverflow);
        resizeObserver.observe(container);
      }
      if (typeof MutationObserver !== "undefined") {
        mutationObserver = new MutationObserver(checkScrollOverflow);
        mutationObserver.observe(container, { childList: true, subtree: true });
      }
    }
    
    return () => {
      window.removeEventListener('resize', checkScrollOverflow);
      clearTimeout(timer);
      if (resizeObserver) resizeObserver.disconnect();
      if (mutationObserver) mutationObserver.disconnect();
    };
  }, [selectedCategory, selectedCore, selectedMainCategory, selectedSubCategory, k12Level]);

  // Convert Blob to Uint8Array for AWS SDK v3
  const blobToUint8Array = async (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (reader.result instanceof ArrayBuffer) {
          resolve(new Uint8Array(reader.result));
        } else {
          reject(new Error('Failed to convert blob to ArrayBuffer'));
        }
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(blob);
    });
  };

  // AWS S3 Upload Function
  const uploadToS3 = async (fileBlob, fileName, contentType = 'application/pdf') => {
    if (!AWS_ACCESS_KEY_ID || !AWS_SECRET_ACCESS_KEY) {
      throw new Error("AWS credentials not configured");
    }

    try {
      // Generate unique S3 key
      const randNo = Math.floor(Math.random() * 9000) + 1000;
      const timestamp = Math.floor(Date.now() / 1000);
      const cleanName = fileName.replace(/\s+/g, '-').replace(/[^A-Za-z0-9-]/g, '');
      const ext = cleanName.includes('.') ? `.${cleanName.split('.').pop()}` : '.pdf';
      const baseName = cleanName.replace(ext, '');
      const s3Key = `${S3_FOLDER}${baseName}_${randNo}_${timestamp}${ext}`;

      // Convert Blob to Uint8Array for AWS SDK v3
      const fileBody = await blobToUint8Array(fileBlob);

      // Create and send S3 command
      const command = new PutObjectCommand({
        Bucket: S3_BUCKET,
        Key: s3Key,
        Body: fileBody,
        StorageClass: 'REDUCED_REDUNDANCY',
        ACL: 'public-read',
        ContentType: contentType,
        Metadata: {
          'uploaded-by': 'react-app',
          'upload-time': new Date().toISOString()
        }
      });

      await s3Client.send(command);
      const s3Url = `https://${S3_BUCKET}.s3.${AWS_REGION}.amazonaws.com/${s3Key}`;
      
      return s3Url;
    } catch (error) {
      throw new Error(`S3 Upload failed: ${error.message}`);
    }
  };

  // Helper function to update flipbook URL in backend
  const updateFlipbookInBackend = async (bookId, flipbookUrl, presentationS3Url, documentId, bookSource) => {
    const apiEndpoints = {
      "Industry": "https://midc.easylearn.org.in/AI_Controller/update_courseware_flipbook",
      "Technology": "https://technology.skillmaster.net.in/AI_Controller/update_courseware_flipbook",
      "Agriculture": "https://agriculture.skillmaster.net.in/AI_Controller/update_courseware_flipbook",
      "Mathematics": "https://mathematics.easylearn.net.in/AI_Controller/update_courseware_flipbook",
      "Vocational": "https://vocational.skillmaster.net.in/AI_Controller/update_courseware_flipbook",
      "UPSC": "https://upsc.skillmaster.net.in/AI_Controller/update_courseware_flipbook",
      "K12": "https://k12.easylearn.org.in/AI_Controller/update_courseware_flipbook",
      "Medical": "https://medical.skillmaster.net.in/AI_Controller/update_courseware_flipbook",
      "Management": "https://management.skillmaster.net.in/AI_Controller/update_courseware_flipbook"
    };

    const apiUrl = apiEndpoints[bookSource];
    
    if (!apiUrl) {
      throw new Error(`Unknown book source: ${bookSource}`);
    }

    try {
      const formData = new URLSearchParams();
      formData.append('book_id', bookId);
      formData.append('flipbook_url', flipbookUrl);
      formData.append('presentation_s3_url', presentationS3Url);
      formData.append('document_id', documentId);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString()
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Backend update failed: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      
      if (result.Response === 'OK') {
        return result.data;
      } else {
        throw new Error(result.message || 'Backend update failed');
      }
    } catch (error) {
      throw error;
    }
  };

  const handleUpdateBook = async (book) => {
    const bookKey = `${book.id}_${book.pdf_name}`;
    const baseUrl = "https://dolphin-app-33jp4.ondigitalocean.app";
    
    setUpdatingBooks(prev => ({ ...prev, [bookKey]: true }));
    
    let flipbookUrl = '';
    let documentId = '';
    let presentationS3Url = '';
    
    try {
      if (!book.pdf_link) {
        throw new Error("No PDF link available for this book");
      }

      // Fetch PDF
      const pdfResponse = await fetch(book.pdf_link);
      if (!pdfResponse.ok) {
        throw new Error(`Failed to fetch PDF: ${pdfResponse.status} ${pdfResponse.statusText}`);
      }
      const pdfBlob = await pdfResponse.blob();

      // Generate PPT
      const pdfFile = new File([pdfBlob], `${book.pdf_name}.pdf`, { type: "application/pdf" });
      
      const queryParams = new URLSearchParams({
        format: "presentation",
        num_cards: "10",
        theme_name: "Pearl",
        additional_instructions: "Focus on clarity and visuals",
        export_as: "pdf",
        text_amount: "medium",
        tone: "professional",
        audience: "students",
        language: "en",
        image_source: "aiGenerated",
        image_model: "dall-e-3",
        image_style: "realistic",
        dimensions: "16x9"
      });
      
      const pptFormData = new FormData();
      pptFormData.append("file", pdfFile);
      
      const pptUrl = `${baseUrl}/presentation/generate_ppt_from_file?${queryParams}`;
      
      const pptResponse = await fetch(pptUrl, {
        method: "POST",
        body: pptFormData,
      });

      if (!pptResponse.ok) {
        const errorText = await pptResponse.text();
        throw new Error(`PPT generation failed: ${pptResponse.status} - ${errorText}`);
      }

      const generateData = await pptResponse.json();
      const generationId = generateData.generation_id;
      
      // Poll for PPT completion
      const maxAttempts = 40;
      let attempts = 0;
      let pptDownloadUrl = null;
      
      while (attempts < maxAttempts) {
        const statusUrl = `${baseUrl}/presentation/get_ppt_url/${generationId}`;
        const statusResponse = await fetch(statusUrl);
        
        if (statusResponse.ok) {
          const statusData = await statusResponse.json();
          
          if (statusData.status === 'completed') {
            pptDownloadUrl = `${baseUrl}/presentation/download_ppt/${generationId}`;
            break;
          } else if (statusData.status === 'failed') {
            throw new Error(`PPT generation failed: ${statusData.message || 'Unknown error'}`);
          }
        }
        
        attempts++;
        await new Promise(resolve => setTimeout(resolve, 5000));
      }

      if (!pptDownloadUrl) {
        throw new Error("PPT generation timed out");
      }

      // Download PPT
      const pptDownloadResponse = await fetch(pptDownloadUrl);
      if (!pptDownloadResponse.ok) {
        throw new Error(`Failed to download PPT: ${pptDownloadResponse.status}`);
      }
      const pptPdfBlob = await pptDownloadResponse.blob();
      const contentType = pptDownloadResponse.headers.get('content-type') || 'application/pdf';

      // Upload to S3
      presentationS3Url = await uploadToS3(pptPdfBlob, `${book.pdf_name}_presentation.pdf`, contentType);

      // Create Heyzine flipbook
      const flipbookOptions = {
        title: `${book.pdf_name} - Presentation Flipbook`,
        description: `Interactive flipbook of the presentation for ${book.pdf_name}`,
        page_flip_sound: 1,
        show_download_pdf: 1,
        show_thumbnails: 1,
        show_share_button: 1,
      };
      
      const flipbookData = await heyzineService.createFlipbook(presentationS3Url, flipbookOptions);
      
      // Extract URLs from response
      flipbookUrl = flipbookData.url;
      documentId = flipbookData.id;
      
      if (!flipbookUrl) {
        throw new Error("Invalid response from Heyzine: No flipbook URL returned");
      }

      // Save to localStorage
      const updatedBooksData = JSON.parse(localStorage.getItem("updatedBooks")) || {};
      updatedBooksData[book.pdf_name] = {
        presentation_s3_url: presentationS3Url,
        flipbook_url: flipbookUrl,
        embed_url: heyzineService.getEmbedCode(flipbookUrl),
        updated_at: new Date().toISOString(),
        document_id: documentId
      };
      localStorage.setItem("updatedBooks", JSON.stringify(updatedBooksData));
      
      // Update backend database
      await updateFlipbookInBackend(
        book.id, 
        flipbookUrl, 
        presentationS3Url, 
        documentId,
        book.source
      );
      
      // Update the local books array
      setBooks(prevBooks => prevBooks.map(b => 
        b.id === book.id ? { ...b, pdf_link: flipbookUrl } : b
      ));
      
      // Open flipbook in new tab
      window.open(flipbookUrl, "_blank");
      
    } catch (error) {
      console.error("Book update failed:", error);
    } finally {
      setUpdatingBooks(prev => {
        const newState = { ...prev };
        delete newState[bookKey];
        return newState;
      });
    }
  };
  

  const topCategories = isAllSelected
    ? ["All", ...mainCategories]
    : savedInterests.length > 0
    ? [...savedInterests]
    : ["All", ...mainCategories];

  // For normal users, override topCategories with admin-visible categories
  const visibleCategories = (() => {
    if (isSuperAdmin) {
      return topCategories; // Superadmin sees everything
    }
    
    // For normal users, build list from mainCategories based on admin config
    const adminVisibleCategories = ["All"]; // Always show "All"
    
    mainCategories.forEach(category => {
      const visibility = adminCourseVisibility[category];
      // ✅ Only show if explicitly set to true
      if (visibility === true) {
        adminVisibleCategories.push(category);
      }
    });
    
    console.log("📋 Admin Config:", adminCourseVisibility);
    console.log("✅ Visible Categories:", adminVisibleCategories);
    
    return adminVisibleCategories;
  })();

  const coreButtons = (() => {
    if (selectedCategory === "All") return [];

    let cores = (coreMap[selectedCategory] || []).sort((a, b) => b.localeCompare(a));

    if (cores.length === 0) return [];
    return isSuperAdmin ? ["All", ...cores] : cores;
  })();

  // K12 "NCERT" view merges every K12 core (International School, Jr. College, ...)
  const isK12AllCores = selectedCategory === "K12" && selectedCore === K12_ALL_CORES;

  const categoryButtons = (() => {
    if (selectedCore === "All") return [];
    const mapForSource = categoryMap[selectedCategory];
    if (mapForSource && (isK12AllCores || mapForSource[selectedCore])) {
      let categories = isK12AllCores
        ? [...new Set(Object.values(mapForSource).flat())]
        : [...mapForSource[selectedCore]];
      categories.sort((a, b) => {
        const getNumber = (str) => {
          const match = str.match(/\d+$/);
          return match ? parseInt(match[0], 10) : 0;
        };
        return getNumber(a) - getNumber(b);
      });
      return isSuperAdmin ? ["All", ...categories] : categories;
    }
    return [];
  })();

  const subCategoryButtons = (() => {
    if (selectedCore === "All" || selectedMainCategory === "All") return [];
    const mapForSource = subCategoryMap[selectedCategory];
    if (isK12AllCores && mapForSource) {
      const subCats = [
        ...new Set(
          Object.values(mapForSource).flatMap((coreMap) => coreMap[selectedMainCategory] || [])
        ),
      ].sort((a, b) => b.localeCompare(a));
      return isSuperAdmin ? ["All", ...subCats] : subCats;
    }
    if (
      mapForSource &&
      mapForSource[selectedCore] &&
      mapForSource[selectedCore][selectedMainCategory]
    ) {
      const subCats = mapForSource[selectedCore][selectedMainCategory].sort((a, b) => b.localeCompare(a));
      return isSuperAdmin ? ["All", ...subCats] : subCats;
    }
    return [];
  })();

  const filteredBooks = books.filter((book) => {
    const matchesSearch =
      (book?.pdf_name || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (book?.sub_category_name || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesCategory =
      selectedCategory === "All" || book.source === selectedCategory;
    const matchesCore =
      selectedCore === "All" ||
      isK12AllCores ||
      (book.core_name || book.voc_core_name || "") === selectedCore;
    const matchesMainCategory =
      selectedMainCategory === "All" ||
      book.category_name === selectedMainCategory ||
      book.voc_category_name === selectedMainCategory;
    const matchesSubCat =
      selectedSubCategory === "All" ||
      (book.sub_category_name || book.voc_sub_category_name || "") ===
        selectedSubCategory;


    // Check if category is visible based on admin settings (only for non-superadmin)
    const categoryVisible = isCategoryVisible(book.source || book.category_name || book.core_name || "");

    return (
      matchesSearch &&
      matchesCategory &&
      matchesCore &&
      matchesMainCategory &&
      matchesSubCat &&
      categoryVisible
    );
  });

  const handleExploreBook = (book) => {
    const purchasedBooks = JSON.parse(localStorage.getItem("purchasedBooks")) || [];
    const isPurchased = purchasedBooks.some((item) => item.pdf_name === book.pdf_name);
    
    // Check if we have a flipbook URL for this book
    const updatedBooks = JSON.parse(localStorage.getItem("updatedBooks")) || {};
    const bookData = updatedBooks[book.pdf_name];

    if (bookData && bookData.flipbook_url) {
      window.open(bookData.flipbook_url, "_blank");
    } else if (bookData && bookData.presentation_s3_url) {
      window.open(bookData.presentation_s3_url, "_blank");
    } else if (isPurchased && book.pdf_link) {
      window.open(book.pdf_link, "_blank");
    } else {
      navigate("/book-detail", { state: { book } });
    }
  };

  return (
    <div className="book-page-container">
      {/* Header & Filters */}
      <section className="book-page-header">
        <div className="book-page-header-content">
          <div className="book-page-filter-wrapper">
          {showLeftArrow && (
            <button
              type="button"
              className="book-page-filter-arrow left"
              onClick={() => scrollFilters(-1)}
              aria-label="Scroll filters left"
            >
              <FaChevronLeft />
            </button>
          )}
          {showScrollArrow && (
            <button
              type="button"
              className="book-page-filter-arrow right"
              onClick={() => scrollFilters(1)}
              aria-label="Scroll filters right"
            >
              <FaChevronRight />
            </button>
          )}
          <div 
            className="book-page-search-filter-container"
            ref={scrollContainerRef}
            onScrollCapture={checkScrollOverflow}
          >
            {/* Category / Core / Subcategory Buttons */}
            {selectedCategory === "All" ? (
              <div className="book-page-category-filters">
                {visibleCategories.map((category) => (
                  <button
                    key={category}
                    onClick={() => {
                      setSelectedCategory(category);
                      setSelectedCore("All");
                      setSelectedMainCategory("All");
                      setSelectedSubCategory("All");
                      setK12Level("All");
                      setVisibleCount(24);
                    }}
                    className={`category-btn ${
                      selectedCategory === category ? "active" : ""
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            ) : selectedCore === "All" ? (
              <div className="book-page-core-filters">
                {(isSuperAdmin || topCategories.length > 1) && (
                  <button
                    onClick={() => {
                      if (selectedCategory === "K12") setK12Level("All");
                      setSelectedCategory("All");
                      setSelectedCore("All");
                      setSelectedMainCategory("All");
                      setSelectedSubCategory("All");
                      setVisibleCount(24);
                    }}
                    className="book-page-back-btn"
                  >
                    <FaArrowLeft size={16} />
                  </button>
                )}
                {selectedCategory === "K12" ? (
                  // K12 content is NCERT only: one label, all K12 books shown together
                  <button
                    className="category-btn"
                    type="button"
                    onClick={() => {
                      setSelectedCore(K12_ALL_CORES);
                      setSelectedMainCategory("All");
                      setSelectedSubCategory("All");
                      setVisibleCount(24);
                    }}
                  >
                    NCERT
                  </button>
                ) : coreButtons.map((core) => (
                  <button
                    key={core}
                    onClick={() => {
                      setSelectedCore(core);
                      setSelectedMainCategory("All");
                      setSelectedSubCategory("All");
                      setVisibleCount(24);
                    }}
                    className={`category-btn ${
                      selectedCore === core ? "active" : ""
                    }`}
                  >
                    {core}
                  </button>
                ))
                }
              </div>
            ) : selectedMainCategory === "All" ? (
              <div className="book-page-main-category-filters">
                <button
                  onClick={() => {
                    setSelectedCore("All");
                    setSelectedMainCategory("All");
                    setSelectedSubCategory("All");
                    setVisibleCount(24);
                  }}
                  className="book-page-back-btn"
                  
                >
                  <FaArrowLeft size={16} />
                </button>
                {categoryButtons.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => {
                      setSelectedMainCategory(cat);
                      setSelectedSubCategory("All");
                      setVisibleCount(24);
                    }}
                    className={`category-btn ${
                      selectedMainCategory === cat ? "active" : ""
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            ) : (
              <div className="book-page-sub-category-filters">
                <button
                  onClick={() => {
                    setSelectedMainCategory("All");
                    setSelectedSubCategory("All");
                    setVisibleCount(24);
                  }}
                  className="book-page-back-btn"
                  
                >
                  <FaArrowLeft size={16} />
                </button>
                {subCategoryButtons.map((subCat) => (
                  <button
                    key={subCat}
                    onClick={() => {
                      setSelectedSubCategory(subCat);
                      setVisibleCount(24);
                    }}
                    className={`category-btn ${
                      selectedSubCategory === subCat ? "active" : ""
                    }`}
                  >
                    {subCat}
                  </button>
                ))}
              </div>
            )}
            {/* Search - MOVED INSIDE CONTAINER */}
            <div className={`search-box ${showScrollArrow ? 'show-arrow' : ''}`}>
              <svg
                className="book-page-search-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search books..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setVisibleCount(24);
                }}
                className="book-page-search-input"
              />
            </div>
          </div>
          </div>
        </div>
      </section>

      {/* Books Grid */}
      <section className="book-page-grid-section">
        <div className="books-container">
          <div className="book-page-grid">
            {filteredBooks.slice(0, visibleCount).map((book) => {
              const purchasedBooks =
                JSON.parse(localStorage.getItem("purchasedBooks")) || [];
              const isPurchased = purchasedBooks.some(
                (item) => item.pdf_name === book.pdf_name
              );
              const bookKey = `${book.id}_${book.pdf_name}`;
              const isUpdating = updatingBooks[bookKey];
              
              // Check if this book has resources
              const updatedBooksData = JSON.parse(localStorage.getItem("updatedBooks")) || {};
              const bookData = updatedBooksData[book.pdf_name];
              const hasFlipbook = bookData?.flipbook_url;
              const hasPresentation = bookData?.presentation_s3_url;

              return (
                <div key={book.id + book.pdf_link} className="book-page-card">
                  <div className="book-page-image-container" style={bookImageBackground(book, 300, 300)}>
                    {hasFlipbook && (
                      <span className="book-page-flipbook-indicator" style={{
                        position: "absolute",
                        top: "5px",
                        right: "5px",
                        background: "#4CAF50",
                        color: "white",
                        padding: "2px 6px",
                        borderRadius: "3px",
                        fontSize: "10px",
                        fontWeight: "bold",
                        zIndex: 1
                      }}>
                        📖 Flipbook
                      </span>
                    )}
                    {hasPresentation && (
                      <span className="book-page-s3-indicator" style={{
                        position: "absolute",
                        top: "25px",
                        right: "5px",
                        background: "#2196F3",
                        color: "white",
                        padding: "2px 6px",
                        borderRadius: "3px",
                        fontSize: "10px",
                        fontWeight: "bold",
                        zIndex: 1
                      }}>
                        📊 PPT
                      </span>
                    )}
                    {isPurchased ? (
                      <span className="book-page-purchased-label">
                        Purchased
                      </span>
                    ) : book.price === "0" || book.price === 0 || !book.price ? (
                      <span className="book-page-free-label">FREE</span>
                    ) : (
                      <span className="book-page-price-label">₹{book.price}</span>
                    )}
                    <img
                      src={bookImageSrc(book, 300, 300)}
                      alt={book.pdf_name}
                      className="book-page-image"
                      loading="lazy"
                      onError={bookImageFallback(book, 300, 300)}
                      onLoad={clearPlaceholderBackground}
                    />
                  </div>
                  <div className="book-page-info">
                    <h3 className="book-page-title">{book.pdf_name}</h3>
                    {book.sub_category_name && book.sub_category_name !== "" && (
                      <p className="book-page-description">
                        Sub-Category: {book.sub_category_name}
                      </p>
                    )}
                    <div className="book-page-footer">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExploreBook(book);
                        }}
                        className="book-page-preview-btn"
                        title={hasFlipbook ? "View Flipbook" : hasPresentation ? "Download PPT PDF" : "Explore Book"}
                      >
                        <FaBookOpen />
                        {hasFlipbook ? "View Flipbook" : hasPresentation ? "Download PPT" : "Explore Book"}
                      </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUpdateBook(book);
                          }}
                          className={`book-page-convert-btn ${isUpdating ? 'updating' : ''}`}
                          disabled={isUpdating}
                          title="Generate PPT → Upload to S3 → Create Flipbook"
                        >
                          {isUpdating ? (
                            <>
                              <FaSync className="book-page-spin-icon" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <FaSync />
                              {hasFlipbook ? "Update" : "Convert"}
                            </>
                          )}
                        </button>
                
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {filteredBooks.length === 0 && (
            <div className="book-page-empty-state">
              {booksStatus === "loading" && books.length === 0 ? (
                <p>Loading books...</p>
              ) : books.length === 0 ? (
                <>
                  <p>We couldn't load books right now. Please check your connection and try again.</p>
                  {failedSources.length > 0 && (
                    <small>Unavailable sources: {failedSources.join(", ")}</small>
                  )}
                  <button onClick={() => setReloadKey((k) => k + 1)}>Try Again</button>
                </>
              ) : (
                <p>No books match your search or filter.</p>
              )}
            </div>
          )}
          {visibleCount < filteredBooks.length && (
            <button
              className="book-page-load-more-btn"
              onClick={() => setVisibleCount(visibleCount + 24)}
            >
              Load More
            </button>
          )}
        </div>
      </section>
    </div>
  );
};

export default Book;